import React, { useEffect, useState } from 'react';

import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { Challenge } from '@repo/editor/src/types';
import { withEventScope } from '@repo/event-processor';
import { IconButton } from '@repo/ui/components/Buttons';
import TemplateBookmarkIcon from '@repo/ui/components/Icon/TemplateBookmarkIcon';
import { themeStyle } from '@repo/ui/theming';

import ChallengeBadge from 'components/Challenges/ChallengeBadge/ChallengeBadge';
import {
  BookmarkContainer,
  ChallengeContainer,
} from 'components/PostPreview/styles';

interface TopLabelsProps {
  templateId: string;
  challenge?: Challenge | null;
  challengePosition?: number | null;
}

const TopLabels: React.FC<TopLabelsProps> = ({
  templateId,
  challenge,
  challengePosition,
}) => {
  const isLoggedIn = useUserStore(userStoreSelector.isLoggedIn);
  const bookmarks = useUserStore((state) => state.bookmarks);

  const [bookmark, setBookmark] = useState<{
    status: 'idle' | 'pending' | 'resolved';
    bookmarked: boolean | null;
  }>({
    status: 'idle',
    bookmarked: null,
  });

  useEffect(() => {
    setBookmark((state) => ({
      ...state,
      bookmarked: bookmarks.includes(templateId),
    }));
  }, [bookmarks, templateId]);

  const handleBookmark = async (event: React.MouseEvent): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();
    setBookmark((state) => ({ ...state, status: 'pending' }));

    const previousBookmarked = bookmark.bookmarked ?? false;

    await useUserStore
      .getState()
      .updateBookmark(previousBookmarked, templateId);

    setBookmark({
      bookmarked: !previousBookmarked,
      status: 'resolved',
    });
  };
  return (
    <>
      {bookmark.bookmarked && (
        <BookmarkContainer activeDefaultColor={themeStyle.inkBase}>
          <IconButton
            ariaLabel="Preview bookmark"
            dataTestId="design-posts--preview-bookmark"
            disabled={!isLoggedIn || bookmark.status === 'pending'}
            eventOptions={{
              action: 'removeBookmark',
            }}
            height="27px"
            icon={TemplateBookmarkIcon}
            name="bookmarkButton"
            theme={{
              color: themeStyle.inkBase,
              activeDefaultColor: themeStyle.inkBase,
              activeColor: themeStyle.inkMain,
            }}
            onClick={handleBookmark}
          />
        </BookmarkContainer>
      )}
      {challenge && challengePosition && challengePosition <= 3 && (
        <ChallengeContainer extraBottomSpace={bookmark.bookmarked}>
          <ChallengeBadge height="24px" position={challengePosition} />
        </ChallengeContainer>
      )}
    </>
  );
};

export default withEventScope(TopLabels, { module: 'postPreviewLabels' });
